import React, { useState } from 'react';
import { ArrowLeft, Plus, Minus, Search, HelpCircle } from 'lucide-react';
import { goHome, goToContact } from '../utils/navigation';

const FAQ: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const goBack = () => {
    goHome();
  };

  const toggleSection = (key: string) => {
    setExpandedSection(prev => prev === key ? null : key);
  };

  const faqGroups = [
    {
      title: 'General Information',
      items: [
        {
          question: "What is Durga Puja Network?",
          answer: "Durga Puja Network (DPN) is a global cultural platform that celebrates and preserves Durga Puja as a vibrant intangible cultural heritage. We connect communities worldwide through art, culture, and tradition, creating a digital archive of celebrations and fostering global cultural connectivity."
        },
        {
          question: "Why is Durga Puja considered UNESCO Intangible Cultural Heritage?",
          answer: "Durga Puja was inscribed on UNESCO's Representative List of the Intangible Cultural Heritage of Humanity in 2021. It was recognized for its inclusive nature, community participation, artistic excellence, and its role in promoting social cohesion and cultural diversity. The festival showcases traditional craftsmanship, performing arts, and ritual practices that have been passed down through generations."
        },
        {
          question: "Is this platform religious or cultural?",
          answer: "DPN is primarily a cultural platform. While Durga Puja has religious origins, our focus is on celebrating its artistic, communal, and cultural dimensions—the craftsmanship, community spirit, traditional arts, and cultural practices that make it a unique global heritage. The platform is inclusive and welcomes people from all backgrounds to appreciate and participate in this rich cultural celebration."
        }
      ]
    },
    {
      title: 'Platform Features',
      items: [
        {
          question: "How can I find Durga Puja celebrations near me?",
          answer: "Use our Search & Discovery feature on the homepage. Enter your location or browse through our curated listings of Durga Puja celebrations worldwide. Each listing includes details about location, dates, expected attendance, and special features."
        },
        {
          question: "Can I submit photos to the gallery?",
          answer: "Yes! We encourage community members to share their Durga Puja moments. Visit our Gallery section and click \"Submit Your Photos\" to contribute to our global cultural archive. All submissions are reviewed to ensure they align with our cultural celebration focus."
        },
        {
          question: "How does the voting system work?",
          answer: "Our voting feature allows community members to support their favorite Puja committees and celebrations. You can vote for categories like Best Pandal Art, Best Committee Spirit, and Best Cultural Program. Voting helps recognize excellence and promotes healthy community engagement."
        },
        {
          question: "What is the Committee Shop?",
          answer: "Our Committee Shop offers authentic, quality essentials for Puja committees and individuals. We source items directly from traditional artisans and suppliers, including sal leaf plates, lotus flowers, traditional bananas, dhunuchi sets, and other ceremonial items."
        }
      ]
    },
    {
      title: 'Committee & Participation',
      items: [
        {
          question: "How can I register my Puja committee?",
          answer: "Fill out our registration form with your committee details, location, and celebration information. Once verified, your Puja will be featured in our global directory, making it discoverable to community members and visitors worldwide."
        },
        {
          question: "Can international communities participate?",
          answer: "Absolutely! DPN celebrates Durga Puja as a global cultural heritage. We welcome and actively support international communities, diaspora celebrations, and cultural organizations worldwide. Our platform is designed to connect celebrations across continents."
        },
        {
          question: "How can I volunteer or contribute?",
          answer: "There are many ways to contribute: submit photos and stories, help with translations, volunteer for community outreach, assist with cultural documentation, or support local committees. Contact us through our registration form expressing your interest in volunteering."
        },
        {
          question: "Do you support cultural education programs?",
          answer: "Yes! We collaborate with schools, universities, and cultural institutions to promote understanding of Durga Puja as cultural heritage. We provide educational resources, cultural guides, and support for academic research on intangible cultural heritage."
        }
      ]
    },
    {
      title: 'Technical Support',
      items: [
        {
          question: "Is the platform free to use?",
          answer: "Yes, DPN is free for community members, visitors, and most Puja committees. We believe cultural heritage should be accessible to everyone. Some premium features for large organizations may have associated costs to support platform maintenance and development."
        },
        {
          question: "How do I report inappropriate content?",
          answer: "We maintain high standards for cultural content. If you encounter inappropriate material, use the report function on any content page, or contact us directly. Our moderation team reviews all reports promptly to maintain the cultural integrity of our platform."
        },
        {
          question: "Can I access the platform on mobile devices?",
          answer: "Yes! DPN is fully responsive and optimized for mobile devices. You can access all features—browsing celebrations, viewing galleries, voting, and registration—seamlessly on smartphones and tablets."
        },
        {
          question: "How do you protect user privacy?",
          answer: "We take privacy seriously and comply with international data protection standards. We only collect necessary information for platform functionality, never share personal data with third parties without consent, and provide full control over your data and privacy settings."
        }
      ]
    },
    {
      title: 'Cultural Heritage & Preservation',
      items: [
        {
          question: "How does DPN contribute to cultural preservation?",
          answer: "We create a comprehensive digital archive of Durga Puja celebrations, document traditional practices, preserve artisan techniques, collect oral histories, and maintain a global database of cultural expressions. This ensures future generations can access and learn from this rich heritage."
        },
        {
          question: "Do you work with UNESCO or other cultural organizations?",
          answer: "We collaborate with various cultural heritage organizations, academic institutions, and community groups. While we celebrate UNESCO's recognition of Durga Puja, we operate independently as a community-driven platform dedicated to cultural preservation and celebration."
        },
        {
          question: "How can artisans showcase their work?",
          answer: "Artisans can feature their work through our gallery, participate in our cultural documentation projects, and connect with Puja committees through our platform. We actively promote traditional craftsmanship and provide visibility to skilled artisans worldwide."
        }
      ]
    }
  ];

  // Filter FAQs based on search term
  const filteredFaqGroups = faqGroups.map(group => ({
    ...group,
    items: group.items.filter(item => 
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(group => group.items.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
      {/* Header Section */}
      <section className="py-12 bg-gradient-to-r from-dpn-olive to-dpn-light-green">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={goBack}
            className="inline-flex items-center text-white hover:text-gray-200 transition-colors duration-200 mb-6 font-lucy-rose font-medium"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </button>
          <div className="text-center text-white">
            <div className="flex justify-center mb-4">
              <HelpCircle className="h-16 w-16" />
            </div>
            <h1 className="font-runtime text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
            <p className="font-lucy-rose text-xl opacity-95 max-w-2xl mx-auto">
              Find answers to common questions about Durga Puja Network and our cultural heritage platform
            </p>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-8 bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-full focus:ring-2 focus:ring-dpn-olive focus:border-transparent outline-none font-lucy-rose text-lg"
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {filteredFaqGroups.map((group, groupIndex) => (
              <div key={group.title} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Group Header */}
                <div className="bg-gradient-to-r from-dpn-olive to-dpn-light-green px-6 py-4">
                  <h2 className="font-runtime text-2xl font-bold text-white">{group.title}</h2>
                </div>

                {/* Group Items */}
                <div className="divide-y divide-gray-200">
                  {group.items.map((item, itemIndex) => {
                    const key = `${groupIndex}-${itemIndex}`;
                    const isOpen = expandedSection === key;
                    return (
                      <div key={key}>
                        <button
                          onClick={() => toggleSection(key)}
                          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <h3 className="font-runtime text-lg font-bold text-black pr-4">
                            {item.question}
                          </h3>
                          {isOpen ? (
                            <Minus className="h-5 w-5 text-dpn-olive flex-shrink-0" />
                          ) : (
                            <Plus className="h-5 w-5 text-dpn-olive flex-shrink-0" />
                          )}
                        </button>
                        
                        {isOpen && (
                          <div className="px-6 pb-6">
                            <div className="border-t border-gray-100 pt-4">
                              <p className="font-lucy-rose text-gray-700 leading-relaxed">
                                {item.answer}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {filteredFaqGroups.length === 0 && searchTerm && (
              <div className="text-center py-12">
                <HelpCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="font-runtime text-xl font-bold text-gray-600 mb-2">No FAQs found</h3>
                <p className="font-lucy-rose text-gray-500">
                  Try searching with different keywords or browse all categories above.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 bg-gradient-to-r from-dpn-pink to-dpn-raspberry">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-runtime text-3xl font-bold text-white mb-4">Still Have Questions?</h2>
          <p className="font-lucy-rose text-xl text-white/95 mb-8">
            Our community support team is here to help you with any questions about Durga Puja Network
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={goToContact}
              className="bg-white text-dpn-raspberry hover:bg-gray-100 font-lucy-rose font-semibold px-8 py-3 rounded-full transition-colors duration-200"
            >
              Contact Support
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;